import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import Button from "@mui/material/Button";
import styles from "./FindingsPopup.module.css";
import { useDispatch } from "react-redux";
import { caseActions } from "@_actions/case.actions";
import { catchError, isComponentDisabled } from "@_utils/helpers";
import { LicenseConstants } from "@_constants/license.constants";
import UploadLicensePopup from "../UploadLicensePopup/UploadLicensePopup";
import { caseConstants } from "@_constants/case.constants";
import { Tooltip, CircularProgress } from "@mui/material";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import Scrollbar from "react-perfect-scrollbar-z";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import "scrollbar.css";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import AreYouSurePopup from "../AreYouSurePopup/AreYouSurePopup";
import { validateLink, getBase64 } from "@_utils/helpers";
import makeStyles from "@mui/styles/makeStyles";

const componetTag = "finding_popup";
// Module-level map to persist cache-busting timestamps across popup close/reopen.
// After a screenshot upload, the API returns the same URL, so the browser serves
// a stale cached image. Storing timestamps here (outside component state) ensures
// the cache buster survives component unmount/remount until a full page refresh.
const screenshotCacheBusters = {};

function FindingsPopup(props) {
  const { findingsData, caseId, overviewData, data } = props;
  const inputDisabled = isComponentDisabled(componetTag, data.state);
  const dispatch = useDispatch();
  const caseReducer = useSelector((state) => state.caseReducer);
  const useStyles = makeStyles({
    helperText: { fontSize: "12px !important" },
  });
  const classes = useStyles();

  const [selectedFinding, setSelectedFinding] = useState({});
  const [uploadPopup, setUploadPopup] = useState(false);
  const [openAreYouSurePopup, setOpenAreYouSurePopup] = useState(false);
  const [areYouSurePopupText, setAreYouSurePopupText] = useState("");
  const [areYouSurePopupAction, setAreYouSurePopupAction] = useState(null);
  const [editingPageLinkIndex, setEditingPageLinkIndex] = useState(null);
  const [editedPageLink, setEditedPageLink] = useState("");
  const [pageLinkError, setPageLinkError] = useState("");
  const [localFindingsData, setLocalFindingsData] = useState([]);
  const [isClickingEditIcon, setIsClickingEditIcon] = useState(false);
  const [uploadingScreenshotFindingId, setUploadingScreenshotFindingId] =
    useState(null);
  const { snackbarProps, showSnackbarError, showSnackbarSuccess } =
    useSnackbar();
  const pageLinkInputRef = useRef(null);
  const uploadingScreenshotFindingIdRef = useRef(null);

  // Appends a cache-busting query param to the screenshot URL if one was
  // recorded after a successful upload, forcing the browser to fetch the new image.
  const getScreenshotUrl = (url, findingId) => {
    const buster = screenshotCacheBusters[findingId];
    if (!buster || !url) return url;
    return `${url}${url.includes("?") ? "&" : "?"}t=${buster}`;
  };

  const resetPageLinkEditingState = () => {
    setEditingPageLinkIndex(null);
    setEditedPageLink("");
    setPageLinkError("");
  };

  useEffect(() => {
    if (findingsData && findingsData.length > 0) {
      setLocalFindingsData(findingsData);
    } else if (props.close) {
      props.close();
    }
    resetPageLinkEditingState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findingsData]);

  const closePopup = () => {
    resetPageLinkEditingState();
    props.close();
  };

  const handleDeleteFindingClick = (index) => {
    setAreYouSurePopupText("Do you really want to delete this finding?");
    setAreYouSurePopupAction(() => () => {
      setOpenAreYouSurePopup(false);
      deleteFinding(index);
    });
    setOpenAreYouSurePopup(true);
  };

  const handleDeleteFindingIconClick = (e, index) => {
    if (
      inputDisabled ||
      (findingsData.length === 1 &&
        data.state === caseConstants.CASE_STATE_ACTIVE)
    ) {
      return;
    }
    e.preventDefault();
    handleDeleteFindingClick(index);
  };

  const handleDeleteLicenseClick = (index, findingId) => {
    setAreYouSurePopupText("Do you really want to delete this license?");
    setAreYouSurePopupAction(() => () => {
      setOpenAreYouSurePopup(false);
      deleteLicense(index, findingId);
    });
    setOpenAreYouSurePopup(true);
  };

  function deleteFinding(index) {
    resetPageLinkEditingState();
    dispatch(caseActions.deleteCaseFinding(caseId, findingsData[index].id));
  }

  useEffect(() => {
    if (caseReducer.status["CASE_DELETE_FINDING"] === "success") {
      dispatch(caseActions.clearStateMessage("CASE_DELETE_FINDING"));
      showSnackbarSuccess("Finding deleted successfully.");
      dispatch(caseActions.getCaseWithChargeDetails(caseId, overviewData));
    } else if (catchError(caseReducer.status["CASE_DELETE_FINDING"])) {
      dispatch(caseActions.clearStateMessage("CASE_DELETE_FINDING"));
      showSnackbarError("Delete finding failed.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, caseReducer.status.CASE_DELETE_FINDING, showSnackbarError]);

  const deleteLicense = (index, findingId) => {
    dispatch(caseActions.deleteCaseLicense(data.id, index, findingId));
  };

  useEffect(() => {
    if (caseReducer.status["CASE_DELETE_LICENSE"] === "success") {
      dispatch(caseActions.clearStateMessage("CASE_DELETE_LICENSE"));
      dispatch(caseActions.getCaseWithChargeDetails(caseId, overviewData));
      dispatch(caseActivityActions.getCaseActivities(caseId));
    } else if (catchError(caseReducer.status["CASE_DELETE_LICENSE"])) {
      dispatch(caseActions.clearStateMessage("CASE_DELETE_LICENSE"));
      showSnackbarError("Delete license(s) failed.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, caseReducer.status.CASE_DELETE_LICENSE, showSnackbarError]);

  useEffect(() => {
    if (caseReducer.status["CASE_ADD_FINDING"] === "success") {
      dispatch(caseActions.clearStateMessage("CASE_ADD_FINDING"));
      dispatch(caseActions.getCaseWithChargeDetails(caseId, overviewData));
      closePopup();
    } else if (catchError(caseReducer.status["CASE_ADD_FINDING"])) {
      dispatch(caseActions.clearStateMessage("CASE_ADD_FINDING"));
      showSnackbarError("Add finding failed.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, caseReducer.status.CASE_ADD_FINDING, showSnackbarError]);

  const handleUploadPopup = (finding) => {
    if (!uploadPopup) setSelectedFinding(finding);
    setUploadPopup(!uploadPopup);
  };

  // Handle file input change
  const handleScreenshotFileChange = async (e, findingId) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      const base64Result = await getBase64(file);
      const base64Data = base64Result?.split(";base64,")[1];

      if (!base64Data) {
        showSnackbarError("Invalid image data.");
        return;
      }

      uploadingScreenshotFindingIdRef.current = findingId;
      setUploadingScreenshotFindingId(findingId);

      const payload = {
        screenshot_image_data: base64Data,
        screenshot_name: file.name,
      };

      dispatch(
        caseActions.updateCaseFindingPageScreenshot(payload, findingId, caseId),
      );
    } catch (error) {
      console.error("Error uploading screenshot:", error);
      uploadingScreenshotFindingIdRef.current = null;
      setUploadingScreenshotFindingId(null);
      showSnackbarError("Failed to upload screenshot.");
    }
  };

  const handleEditPageLink = (index, currentPageLink) => {
    setEditingPageLinkIndex(index);
    setEditedPageLink(currentPageLink || "");
    setPageLinkError("");
  };

  const handleCancelEditPageLink = () => {
    resetPageLinkEditingState();
  };

  const handlePageLinkInputBlur = (index) => {
    if (!isClickingEditIcon && editingPageLinkIndex === index) {
      handleSavePageLink(index, findingsData[index].id);
    }
    setIsClickingEditIcon(false);
  };

  const handlePageLinkInputKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSavePageLink(index, findingsData[index].id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      handleCancelEditPageLink();
    }
  };

  const handleSavePageLink = (index, findingId) => {
    const newPageLink = editedPageLink.trim();

    if (!newPageLink) {
      setPageLinkError("Page link is required");
      return;
    }

    if (!validateLink(newPageLink)) {
      setPageLinkError("Please enter a valid page link");
      return;
    }

    setPageLinkError("");
    const data = {
      id: findingId,
      page_link: newPageLink,
    };

    // Optimistically update local state
    const updatedFindings = [...localFindingsData];
    if (updatedFindings[index]) {
      updatedFindings[index] = {
        ...updatedFindings[index],
        page_link: newPageLink,
      };
      setLocalFindingsData(updatedFindings);
    }

    dispatch(caseActions.updateCaseFindingPageLink(caseId, index, data));
    resetPageLinkEditingState();
  };

  useEffect(() => {
    if (caseReducer.status["CASE_UPDATE_FINDING_PAGE_LINK"] === "success") {
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_FINDING_PAGE_LINK"));
      dispatch(caseActions.getCaseWithChargeDetails(caseId, overviewData));
    } else if (
      catchError(caseReducer.status["CASE_UPDATE_FINDING_PAGE_LINK"])
    ) {
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_FINDING_PAGE_LINK"));
      showSnackbarError("Update page link failed.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    caseReducer.status.CASE_UPDATE_FINDING_PAGE_LINK,
    showSnackbarError,
  ]);

  useEffect(() => {
    if (
      caseReducer.status["CASE_UPDATE_FINDING_PAGE_SCREENSHOT"] === "success"
    ) {
      const findingId = uploadingScreenshotFindingIdRef.current;
      if (findingId) {
        screenshotCacheBusters[findingId] = Date.now();
      }
      uploadingScreenshotFindingIdRef.current = null;
      setUploadingScreenshotFindingId(null);
      dispatch(
        caseActions.clearStateMessage("CASE_UPDATE_FINDING_PAGE_SCREENSHOT"),
      );
      dispatch(caseActions.getCaseWithChargeDetails(caseId, overviewData));
    } else if (
      catchError(caseReducer.status["CASE_UPDATE_FINDING_PAGE_SCREENSHOT"])
    ) {
      uploadingScreenshotFindingIdRef.current = null;
      setUploadingScreenshotFindingId(null);
      dispatch(
        caseActions.clearStateMessage("CASE_UPDATE_FINDING_PAGE_SCREENSHOT"),
      );
      showSnackbarError("Update page screenshot failed.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    caseReducer.status.CASE_UPDATE_FINDING_PAGE_SCREENSHOT,
    showSnackbarError,
  ]);

  return (
    <DialogPopup title="Image uses" open={props.open} onClose={closePopup}>
      <div>
        <Scrollbar maxHeight="74vh">
          <div className={styles.tabs_content_container}>
            <div className={styles.tab_content}>
              <div className={styles.add_finding_container}></div>

              {localFindingsData &&
                localFindingsData.map((finding, index) => (
                  <div
                    key={finding.id || index}
                    className={styles.finding_container}
                  >
                    <div className={styles.finding_images_container}>
                      <div className={styles.image_container}>
                        <div className={styles.image_wrapper}>
                          <span className={styles.image_label}>
                            Original image
                          </span>
                          <img
                            className={styles.finding_image}
                            src={finding.original_image.original_image_url}
                            alt={""}
                            onClick={() =>
                              window.open(
                                finding.original_image.original_image_url,
                                "_blank",
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className={styles.image_container}>
                        <div className={styles.screenshot_box}>
                          <span className={styles.image_label}>
                            Page screenshot
                          </span>
                          <div className={styles.screenshot_image_wrapper}>
                            {!inputDisabled && (
                              <>
                                <div className={styles.screenshot_edit}>
                                  <Tooltip
                                    title="Change screenshot"
                                    placement="top"
                                  >
                                    <label
                                      htmlFor={`screenshot-input-${index}`}
                                      className={`${styles.edit_icon_overlay} ${
                                        uploadingScreenshotFindingId ===
                                        finding.id
                                          ? styles.edit_icon_overlay_disabled
                                          : ""
                                      }`}
                                    >
                                      <EditIcon />
                                    </label>
                                  </Tooltip>
                                </div>
                                <input
                                  type="file"
                                  id={`screenshot-input-${index}`}
                                  accept=".jpg, .jpeg, .png, .gif"
                                  style={{ display: "none" }}
                                  onChange={(e) =>
                                    handleScreenshotFileChange(e, finding.id)
                                  }
                                />
                              </>
                            )}
                            {uploadingScreenshotFindingId === finding.id && (
                              <div
                                className={styles.screenshot_loading_overlay}
                              >
                                <CircularProgress color="primary" />
                              </div>
                            )}
                            <img
                              className={styles.finding_image}
                              src={getScreenshotUrl(
                                finding.page_screenshot_url,
                                finding.id,
                              )}
                              alt={""}
                              onClick={() =>
                                window.open(
                                  getScreenshotUrl(
                                    finding.page_screenshot_url,
                                    finding.id,
                                  ),
                                  "_blank",
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.finding_info_container}>
                      <div className={styles.info_row}>
                        <span className={styles.info_label}>
                          Original image:
                        </span>
                        <span className={styles.info_label_value}>
                          {finding.original_image.filename}
                        </span>
                      </div>
                      <div className={styles.info_row}>
                        {editingPageLinkIndex === index ? (
                          <TextfieldInput
                            className={styles.page_link_input_small}
                            inputRef={pageLinkInputRef}
                            label="Page link"
                            value={editedPageLink}
                            onChange={(e) => setEditedPageLink(e.target.value)}
                            onKeyDown={(e) =>
                              handlePageLinkInputKeyDown(e, index)
                            }
                            onBlur={() => handlePageLinkInputBlur(index)}
                            error={!!pageLinkError}
                            helperText={pageLinkError}
                            FormHelperTextProps={{
                              className: classes.helperText,
                            }}
                            margin="none"
                          />
                        ) : (
                          <>
                            <span className={styles.info_label}>
                              Page link:
                            </span>
                            <div className={styles.page_link_container}>
                              <a
                                className={styles.info_value_link}
                                href={finding.page_link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {finding.page_link}
                              </a>
                              {!inputDisabled && (
                                <EditIcon
                                  className={styles.edit_icon_inline}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setIsClickingEditIcon(true);
                                  }}
                                  onClick={() => {
                                    handleEditPageLink(
                                      index,
                                      finding.page_link,
                                    );
                                    setIsClickingEditIcon(false);
                                  }}
                                />
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div className={styles.info_row}>
                        <span className={styles.info_label}>License: </span>
                        <div className={styles.license_container}>
                          {finding.license &&
                          finding.license.id &&
                          (finding.license.license_url ||
                            finding.license.note) ? (
                            <>
                              {/* License row: File link or "no license file uploaded" + status + delete icon */}
                              <div className={styles.license_row}>
                                <div className={styles.license_content}>
                                  {finding.license.license_url ? (
                                    <a
                                      className={styles.info_value_link}
                                      href={finding.license.license_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {finding.license.filename}
                                    </a>
                                  ) : (
                                    <span className={styles.info_value}>
                                      {"<no license file uploaded>"}
                                    </span>
                                  )}
                                </div>
                                <div className={styles.license_actions}>
                                  <span className={styles.license_status}>
                                    {" "}
                                    (
                                    {finding.license.state ===
                                    LicenseConstants.STATE_VALID
                                      ? "Valid"
                                      : finding.license.state ===
                                          LicenseConstants.STATE_INVALID
                                        ? "Invalid"
                                        : "Unchecked"}
                                    )
                                  </span>
                                  {!inputDisabled &&
                                    finding.license &&
                                    finding.license.id && (
                                      <DeleteIcon
                                        className={styles.delete_icon_inline}
                                        onClick={() =>
                                          handleDeleteLicenseClick(
                                            index,
                                            finding.id,
                                          )
                                        }
                                      />
                                    )}
                                </div>
                              </div>
                              {finding.license.note && (
                                <span className={styles.info_value}>
                                  {finding.license.note}
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              {!inputDisabled ? (
                                <Button
                                  className={styles.btn_confirm_small}
                                  size="small"
                                  onClick={() => handleUploadPopup(finding)}
                                >
                                  Add License
                                </Button>
                              ) : (
                                <span className={styles.info_value}>
                                  No license
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`action_container ${styles.finding_action_container}`}
                    >
                      {!inputDisabled && (
                        <>
                          <Tooltip
                            title={
                              inputDisabled ||
                              (findingsData.length === 1 &&
                                data.state === caseConstants.CASE_STATE_ACTIVE)
                                ? "Cannot delete the last finding of an active case"
                                : ""
                            }
                            placement="top"
                            arrow
                          >
                            <span>
                              <DeleteIcon
                                className={`${styles.delete_icon} ${
                                  inputDisabled ||
                                  (findingsData.length === 1 &&
                                    data.state ===
                                      caseConstants.CASE_STATE_ACTIVE)
                                    ? styles.delete_icon_disabled
                                    : ""
                                }`}
                                onClick={(e) =>
                                  handleDeleteFindingIconClick(e, index)
                                }
                              />
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </Scrollbar>
      </div>

      <div className="action_container">
        <Button className="btn_cancel" onClick={closePopup}>
          Close
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <AreYouSurePopup
        open={openAreYouSurePopup}
        close={() => {
          setOpenAreYouSurePopup(false);
          setAreYouSurePopupText("");
          setAreYouSurePopupAction(null);
        }}
        onClick={areYouSurePopupAction}
        title="Confirmation required"
      >
        {areYouSurePopupText}
      </AreYouSurePopup>

      <UploadLicensePopup
        open={uploadPopup}
        close={handleUploadPopup}
        findingId={selectedFinding?.id}
        caseId={caseId}
        overviewData={overviewData}
      />

      <AppSnackbar {...snackbarProps} />
    </DialogPopup>
  );
}

export default FindingsPopup;
